import React, { ComponentType, useState } from "react";
import ServiceWorldCaseItem from "./ServiceWorldCaseItem";
import { Button, CardActionArea, Collapse, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { ArrowForward, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { splitUpByCompletedStatus } from "@/src/services/serviceWorldService";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { Link } from "react-router";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { useFlexServiceLayoutForCustomer } from "@/src/services/advocardService";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import ProductBoxGrid from "@/src/components/ProductBoxGrid/ProductBoxGrid";
import ServiceWorldMyCasesNewCaseCard from "@/src/components/Serviceworld/MyCases/ServiceWorldMyCasesNewCaseCard";
import { Box } from "@mui/system";

type ServiceWorldMyCaseProps = {
  NotificationComponent?: ComponentType<{ minimalCase: MinimalCase }> | null;
  listItemButtonConfig?: { getLink?: Function; handleClick?: Function };
};

const ServiceWorldMyCases = ({ NotificationComponent, listItemButtonConfig }: ServiceWorldMyCaseProps) => {
  const [expandClosedCases, setExpandClosedCases] = useState(true);
  const { customer } = useCustomer();
  const { data: minimalCases, isLoading } = useQuery({
    queryKey: queryKeys.collection("/collection/cases"),
    queryFn: () => fetchCollection("/collection/cases"),
  });

  const isFlexServiceCustomer = useFlexServiceLayoutForCustomer(customer);

  if (isLoading) {
    return (
      <>
        <MyCasesHeader />
        <MyCasesLoader />
      </>
    );
  }

  const { cases, closedCases } = splitUpByCompletedStatus(minimalCases);

  const CasesStatus = () => {
    if (cases.length > 0) {
      return null;
    }

    return (
      <ContentBox
        headline={"Sie haben aktuell keinen laufenden Fall"}
        showDivider={true}
        sx={(theme) => ({
          backgroundColor: theme.palette.primaryPale,
        })}
      >
        {!isFlexServiceCustomer && (
          <ProductBoxGrid
            items={[
              <Typography key="text">
                Legen Sie einen neuen Fall an und <br />
                wir kümmern uns um Ihr Anliegen.
              </Typography>,
              <Button
                key="button"
                variant="contained"
                component={Link}
                to={"/servicewelt/neuer-fall"}
                endIcon={<ArrowForward />}
                sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
              >
                Neuen Fall anlegen
              </Button>,
            ]}
          />
        )}
      </ContentBox>
    );
  };

  return (
    <>
      <Box sx={{ marginBottom: "2rem" }}>
        <MyCasesHeader />
      </Box>
      <CasesStatus />

      <Stack spacing={2}>
        {cases.length > 0 &&
          cases.map((minimalCase) => (
            <ServiceWorldCaseItem
              key={minimalCase.id}
              minimalCase={minimalCase}
              NotificationComponent={NotificationComponent}
              listItemButtonConfig={listItemButtonConfig}
            />
          ))}

        {closedCases.length > 0 && (
          <>
            <Paper elevation={4} sx={(theme) => ({ backgroundColor: theme.palette.background.default })}>
              <CardActionArea onClick={() => setExpandClosedCases((prev) => !prev)} sx={{ padding: "1.5rem" }}>
                <Grid container spacing={2} sx={{ justifyContent: "space-between", alignItems: "center" }}>
                  <Grid>
                    <Typography variant={"h7"}>Abgeschlossene Fälle</Typography>
                  </Grid>
                  <Grid sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-start", md: "flex-end" } }}>
                    {expandClosedCases ? <ExpandLess /> : <ExpandMore />}
                  </Grid>
                </Grid>
              </CardActionArea>
            </Paper>

            <Collapse in={expandClosedCases}>
              <Stack spacing={2}>
                {closedCases.map((minimalCase) => (
                  <ServiceWorldCaseItem
                    key={minimalCase.id}
                    minimalCase={minimalCase}
                    NotificationComponent={NotificationComponent}
                    listItemButtonConfig={listItemButtonConfig}
                  />
                ))}
              </Stack>
            </Collapse>
          </>
        )}

        {cases.length > 0 && !isFlexServiceCustomer && <ServiceWorldMyCasesNewCaseCard />}
      </Stack>
    </>
  );
};

export default ServiceWorldMyCases;

const MyCasesHeader = () => (
  <>
    <Typography component={"h1"} variant={"h5"}>
      Meine Fälle
    </Typography>
    <Typography>Hier finden Sie eine Übersicht Ihrer laufenden Fälle.</Typography>
  </>
);

const MyCasesLoader = () => {
  return (
    <Stack spacing={2}>
      {Array.from({ length: 2 }).map((_, idx) => (
        <ContentBox key={idx}>
          <Skeleton width="40%" height="2rem" sx={{ marginBottom: "1rem" }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Skeleton width={"80%"} height={"1.5rem"} />
              <Skeleton width={"60%"} height={"1.5rem"} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
              <Skeleton variant="rectangular" width={"7.5rem"} height={"2.5rem"} />
            </Grid>
          </Grid>
        </ContentBox>
      ))}
    </Stack>
  );
};
